<template>
  <v-container>
    <v-row align="center">
      <v-col cols="auto">
        <v-btn
          icon
          :to="{ name: 'projects' }"
          exact
        >
          <v-icon small>
            fas fa-arrow-left
          </v-icon>
        </v-btn>
      </v-col>
      <v-col cols="auto">
        <div class="title text-uppercase font-weight-medium">
          {{ !project ? 'Create new project' : 'Edit project' }}
        </div>
      </v-col>
      <v-spacer />
      <v-col
        v-if="project"
        cols="auto"
      >
        <v-btn
          color="error"
          text
          class="text-none"
          @click="deleteConfirmationDialogVisible = true"
        >
          <v-icon left>
            fas fa-trash
          </v-icon>
          Delete
        </v-btn>
      </v-col>
    </v-row>

    <v-divider class="mb-10" />

    <validation-observer ref="validation">
      <v-row no-gutters>
        <v-col
          cols="12"
          md="8"
        >
          <div class="mb-2 font-weight-bold">
            Title
          </div>
          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <v-text-field
              v-model="dirtyProject.title"
              outlined
              label="Title"
              solo
              :error-messages="errors"
              @change="createSlug"
            />
          </validation-provider>

          <div class="mb-2 font-weight-bold">
            Excerpt
          </div>
          <v-textarea
            v-model="dirtyProject.excerpt"
            outlined
            solo
            label="Excerpt"
            counter
            maxlength="200"
            rows="2"
          />

          <div v-if="uploadUrl">
            <div class="mb-2">
              Project Cover Image (optional)
            </div>
            <file-uploader
              ref="fileUploader"
              v-model="fileUpload"
              custom-drag-handler="#handleDropUpload"
              accept="image/*"
              auto
              :upload-url="uploadUrl"
              class="mb-6"
              :additional-form-data="{ }"
              @upload="handleMainImageUpload"
            />
          </div>

          <div class="mb-2 font-weight-bold">
            YouTube Video URL (optional)
          </div>
          <v-text-field
            v-model="dirtyProject.video_url"
            outlined
            solo
            label="https://youtu.be/..."
            hint="Leave empty if the project has no video"
          />

          <div class="mb-2 font-weight-bold mt-4">
            Credits
          </div>
          <div
            v-for="(credit, i) in dirtyProject.credits"
            :key="i"
            class="d-flex align-center mb-2"
            style="gap: 8px;"
          >
            <v-text-field
              v-model="credit.label"
              dense
              outlined
              hide-details
              label="Label"
              style="max-width: 280px;"
            />
            <v-text-field
              v-model="credit.value"
              dense
              outlined
              hide-details
              label="Value"
            />
            <v-btn
              icon
              color="error"
              @click="dirtyProject.credits.splice(i, 1)"
            >
              <v-icon small>
                fas fa-times
              </v-icon>
            </v-btn>
          </div>
          <v-btn
            small
            text
            color="primary"
            class="mb-6"
            @click="dirtyProject.credits.push({ label: '', value: '' })"
          >
            <v-icon
              left
              small
            >
              fas fa-plus
            </v-icon>
            Add credit
          </v-btn>

          <div class="mb-2 font-weight-bold">
            Sort Order (higher shows first)
          </div>
          <v-text-field
            v-model.number="dirtyProject.sort_order"
            outlined
            solo
            type="number"
            label="Sort Order"
          />

          <div class="mb-2 font-weight-bold">
            URL Slug (required)
          </div>
          <validation-provider
            v-slot="{ errors }"
            rules="required"
          >
            <v-text-field
              v-model="dirtyProject.slug"
              outlined
              label="Slug"
              solo
              :error-messages="errors"
            />
          </validation-provider>

          <div class="mb-2 font-weight-bold">
            Meta Description
          </div>
          <v-textarea
            v-model="dirtyProject.meta_description"
            outlined
            solo
            label="Meta Description"
            counter
            maxlength="200"
            rows="2"
          />

          <div class="mt-6">
            <v-btn
              x-large
              color="primary"
              :loading="saving"
              @click="storeProject()"
            >
              Save project
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </validation-observer>

    <confirm-delete-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :deleting="deleting"
      @confirm="deleteProject()"
    />
  </v-container>
</template>

<script>
import Projects from '@/services/Projects'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'
import FileUploader from '@/components/helpers/FileUploader.vue'
import { mixin as fileAgent } from 'vue-file-agent'
import { kebabCase } from 'lodash'

export default {
  name: 'Project',
  components: {
    FileUploader,
    ConfirmDeleteDialog
  },
  mixins: [deleteConfirmationMixin, fileAgent],
  data() {
    return {
      project: undefined,
      loading: false,
      dirtyProject: {
        title: '',
        excerpt: '',
        image_url: null,
        video_url: '',
        credits: [],
        sort_order: 0,
        slug: '',
        meta_description: ''
      },
      fileUpload: [],
      saving: false
    }
  },
  watch: {
    '$route.params.id': function () {
      this.fetchProject()
    }
  },
  created() {
    this.fetchProject()
    this.uploadUrl = process.env.VUE_APP_API_URL + '/projects/upload-image'
  },
  methods: {
    async fetchProject() {
      if (!this.$route.params.id) return

      this.loading = true

      try {
        this.project = await Projects.get(this.$route.params.id)

        this.dirtyProject.title = this.project.title
        this.dirtyProject.excerpt = this.project.excerpt
        this.dirtyProject.video_url = this.project.video_url
        this.dirtyProject.credits = Array.isArray(this.project.credits) ? this.project.credits : []
        this.dirtyProject.sort_order = this.project.sort_order || 0
        this.dirtyProject.slug = this.project.slug
        this.dirtyProject.meta_description = this.project.meta_description
      } catch (e) {
        console.error(e)
      } finally {
        this.loading = false
      }
    },
    async storeProject() {
      const validation = await this.$refs.validation.validate()

      if (!validation) return

      this.saving = true

      try {
        if (this.project) {
          await Projects.update(this.project.id, this.dirtyProject)
        } else {
          await Projects.store(this.dirtyProject)
        }

        this.$store.dispatch('alert', {
          text: 'Successfully saved project',
          color: 'success'
        })

        this.$router.push({ name: 'projects' })
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Something went wrong',
          color: 'error'
        })
        throw e
      } finally {
        this.saving = false
      }
    },
    async deleteProject() {
      this.deleting = true

      try {
        await Projects.delete(this.project.id)

        this.$store.dispatch('alert', {
          text: 'Successfully deleted',
          color: 'success'
        })

        this.$router.push({ name: 'projects' })
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Something went wrong',
          color: 'error'
        })
        throw e
      } finally {
        this.deleteConfirmationDialogVisible = false
        this.deleting = false
      }
    },
    handleMainImageUpload(files) {
      if (!files[0] || !files[0]?.data?.location) {
        this.dirtyProject.image_url = null
      } else {
        this.dirtyProject.image_url = files[0]?.data?.location
      }
    },
    createSlug(title) {
      if (!title || title === '' || this.project) return

      this.dirtyProject.slug = kebabCase(title)
    }
  }
}
</script>
